import { HStack, Stack } from '@metservicedev/met-ui';
import React from 'react';
import { useFormContext } from 'react-hook-form';

import { CloudMovements, CloudMovementText } from '@vona/shared/CloudMovement';
import { HeightFromTypes, HeightFromTypeText, HeightUnits } from '@vona/shared/Height';

import { createStyles } from '../tss';
import { FormVona, RawFormVona } from '../types/FormVona';
import { FormCheckbox } from './FormCheckbox';
import { FormSelect } from './FormSelect';
import { FormTextInput } from './FormTextInput';
import { InfoToolTip } from './InfoToolTip';
import { ValidationContainer } from './ValidationContainer';

const useStyles = createStyles(() => ({
  heightValue: {
    borderBottomRightRadius: 0,
    borderTopRightRadius: 0,
  },
  heightUnit: {
    '& button': {
      borderRadius: 0,
    },
  },
  heightUnitForm: {
    marginLeft: -1,
    minWidth: 96,
    maxWidth: 96,
    '&:has(:focus), &:has(:hover), &:has(:is([open],[data-open],[data-state=open]))': {
      zIndex: 1,
    },
  },
  heightFrom: {
    '& button': {
      borderBottomLeftRadius: 0,
      borderTopLeftRadius: 0,
    },
  },
  heightFromForm: {
    marginLeft: -1,
    minWidth: 110,
    maxWidth: 110,
    '&:has(:focus), &:has(:hover), &:has(:is([open],[data-open],[data-state=open]))': {
      zIndex: 1,
    },
  },
  cloudMovement: {
    justifyContent: 'space-between',
  },
}));

export const CloudInputs: React.FC = () => {
  const { classes } = useStyles();
  const form = useFormContext<FormVona>();
  const cloudState = form.watch('cloud.state');
  const isCloudPresent = cloudState !== 'NO';
  const isCloudHeightKnown = cloudState === 'YES_HEIGHT_KNOWN';
  return (
    <>
      <FormSelect<FormVona, 'cloud.state'>
        data-testid="select-cloud-state"
        dependants={[
          'cloud.height.value',
          'cloud.height.unit',
          'cloud.height.from',
          'cloud.height.extendsAbove',
          'cloud.height.source',
          'cloud.movement',
        ]}
        fieldPath="cloud.state"
        items={[
          { label: 'No cloud present', value: 'NO' },
          { label: 'Cloud present, height unknown', value: 'YES_HEIGHT_UNKNOWN' },
          { label: 'Cloud present, height known', value: 'YES_HEIGHT_KNOWN' },
        ] satisfies { label: string; value: RawFormVona['cloud']['state'] }[]}
        label="Volcanic Cloud Status"
        tooltipContent="Indicates whether a volcanic cloud is present and if the height is known."
      />
      <ValidationContainer hstack>
        <HStack alignItems="start" gap={0} width="100%">
          <FormTextInput
            data-testid="input-cloud-height-value"
            disabled={!isCloudHeightKnown}
            fieldPath="cloud.height.value"
            label="Volcanic Cloud Height"
            textInputProps={{ className: classes.heightValue }}
          />
          <FormSelect
            data-testid="select-cloud-height-unit"
            dependants={['cloud.height.value']}
            disabled={!isCloudHeightKnown}
            fieldPath="cloud.height.unit"
            items={HeightUnits.map((unit) => ({ label: unit, value: unit }))}
            label="Unit"
            formControlProps={{ className: classes.heightUnitForm }}
            selectProps={{ className: classes.heightUnit }}
          />
          <FormSelect
            data-testid="select-cloud-from"
            disabled={!isCloudHeightKnown}
            fieldPath="cloud.height.from"
            items={HeightFromTypes.map((type) => ({
              label: HeightFromTypeText[type],
              value: type,
            }))}
            label="From"
            formControlProps={{ className: classes.heightFromForm }}
            selectProps={{ className: classes.heightFrom }}
          />
        </HStack>
        <InfoToolTip
          data-testid="tooltip-cloud-height"
          tooltipContent={(
            <Stack gap={1}>
              <span>Volcanic ash cloud vertical extent height.</span>
              <span>The units of the cloud height.</span>
              <span>The reference point for where the height measurement is taken from.</span>
            </Stack>
          )}
        />
      </ValidationContainer>
      <FormCheckbox
        data-testid="checkbox-cloud-extends-above"
        fieldPath="cloud.height.extendsAbove"
        label="Cloud extends above the specified height"
        disabled={!isCloudHeightKnown}
        checkboxProps={{ width: 'full' }}
        tooltipContent="Used when top of volcanic cloud is obscured by meteorological cloud."
      />
      <FormTextInput
        data-testid="input-cloud-height-source"
        disabled={!isCloudHeightKnown}
        fieldPath="cloud.height.source"
        label="Height Source"
        tooltipContent="Source of height data (e.g. ground observer, air-report,
          radar, LIDAR, satellite, webcam, etc.)."
      />
      <ValidationContainer hstack classes={{ content: classes.cloudMovement }}>
        <FormSelect
          data-testid="select-cloud-movement"
          disabled={!isCloudPresent}
          fieldPath="cloud.movement"
          items={CloudMovements.map((movement) => ({ label: CloudMovementText[movement], value: movement }))}
          label="Cloud Movement"
          formControlProps={{
            style: { minWidth: '192px', maxWidth: '192px' },
          }}
        />
        <InfoToolTip
          data-testid="tooltip-cloud-movement"
          tooltipContent="Direction of movement of the observed cloud."
        />
      </ValidationContainer>
    </>
  );
};
